import { useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { JobEmailCcCard } from "@/pages/dashboard/fs/JobEmailCc";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Loader2, Calculator, Upload, Sparkles, Trash2, Plus, ShieldCheck, PenLine, Building2, Save,
} from "lucide-react";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

// Each card folds under a chevron (open state remembered per browser); the first opens by default
// on desktop, everything is folded on a phone so the tab stays scannable.
const Card = ({ title, sub, icon, children, testid, open = false }) => (
  <CollapsibleCard id={`fs.acct.${testid}`} icon={icon} title={title} testid={testid} defaultOpen={open} mobileDefaultOpen={false}>
    {sub && <p className="mb-3 mt-3 text-xs leading-relaxed text-ink/50">{sub}</p>}
    {!sub && <div className="mt-3" />}
    {children}
  </CollapsibleCard>
);

export default function AccountingSettings({ tenantId, readOnly, companyId }) {
  const [codes, setCodes] = useState(null);
  const [st, setSt] = useState(null);
  const [staff, setStaff] = useState([]);
  const [fee, setFee] = useState(null);
  const [newCode, setNewCode] = useState({ code: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [defaults, setDefaults] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get(`/tenants/${tenantId}/field-services/gl-codes`),
      api.get(`/tenants/${tenantId}/field-services/settings`),
    ]);
    setCodes(a.data); setDefaults(a.data.gl_defaults || {});
    setSt(b.data.settings); setStaff(b.data.staff || []); setFee(b.data.fee_policy || null);
  }, [tenantId]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the settings")); }, [load]);

  if (!codes || !st) return <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  const saveSettings = (patch, msg) => run(async () => {
    const { data } = await api.put(`/tenants/${tenantId}/field-services/settings`, patch);
    setSt(data.settings);
  }, msg);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/gl-codes/import`, fd);
      toast.success(`${data.added} new, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      await load();
    } catch (err) { toast.error(errMsg(err, "Couldn't read that file")); }
    setBusy(false);
    e.target.value = "";
  };

  const accounting = !!st.accounting_codes;
  return (
    <div className="space-y-4" data-testid="fs-accounting-settings">
      {/* iter370 — off by default. A self-managed HOA has no chart of accounts; a management company turns this on. */}
      <div data-testid="accounting-switch-card" className="flex items-start justify-between gap-4 rounded-2xl border border-sand-200 bg-white p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold text-ink"><Calculator className="h-4 w-4 text-ink/45" /> Accounting codes & management fee</div>
          <p className="mt-1 text-xs leading-relaxed text-ink/55">
            On: every job is charged to a code, invoices inherit it, and your management company's fee is calculated and exported as its own line.
            Off: none of that appears anywhere — jobs are just jobs. Most self-managed {V.lcs("org")} leave this off.
          </p>
        </div>
        <label className="mt-1 inline-flex shrink-0 cursor-pointer items-center gap-2.5">
          <Switch data-testid="accounting-toggle" checked={accounting} disabled={readOnly || busy}
            onCheckedChange={(v) => saveSettings({ accounting_codes: v }, v ? "Accounting codes on" : "Accounting codes off")} />
          <span className="text-sm font-semibold text-ink">{accounting ? "On" : "Off"}</span>
        </label>
      </div>

      <JobEmailCcCard st={st} setSt={setSt} staff={staff} readOnly={readOnly} busy={busy} companyName={companyId ? "Your management company" : ""}
        onSave={() => saveSettings({
          schedule_email_enabled: st.schedule_email_enabled !== false, schedule_email_board: st.schedule_email_board !== false,
          schedule_email_managers: !!st.schedule_email_managers, schedule_cc: st.schedule_cc || [], schedule_cc_users: st.schedule_cc_users || [],
        }, "Saved — they'll be copied from the next job on")} />

      {/* iter372 — may the board open jobs? Off by default: Work orders stays read-only for them. */}
      <div data-testid="board-jobs-card" className="flex items-start justify-between gap-4 rounded-2xl border border-sand-200 bg-white p-4">
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink">{V.many("board")} can open jobs</div>
          <p className="mt-1 text-xs leading-relaxed text-ink/55">
            When on, {V.a("board")} gets the same New-job wizard you do — what, where, scope, and which company — and can
            write or change the scope on a job. Dispatching crews, approving vendors and money stay with managers.
          </p>
        </div>
        <label className="mt-1 inline-flex shrink-0 cursor-pointer items-center gap-2 text-xs font-semibold text-ink">
          <input type="checkbox" data-testid="board-jobs-toggle" checked={!!st.board_can_open_jobs} disabled={readOnly || busy}
            onChange={(e) => saveSettings({ board_can_open_jobs: e.target.checked },
              e.target.checked ? `${V.many("board")} can now open jobs` : `${V.many("board")} are back to read-only on jobs`)} />
          {st.board_can_open_jobs ? "On" : "Off"}
        </label>
      </div>

      {accounting && (<>
      <Card title="Accounting codes" icon={Calculator} testid="gl-codes-card" open
        sub="Every job carries the budget line it's charged to, so an invoice never arrives without a home. Adopt the standard chart, import yours, or type them in.">
        {!codes.codes.length && (
          <div className="mb-3 rounded-xl border border-dashed border-sand-300 bg-sand-50/60 p-4 text-center">
            <p className="text-sm font-semibold text-ink">No codes yet</p>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-ink/50">
              Start from the standard {V.one("org_short")} chart of accounts — you can rename anything that doesn't match your book.
            </p>
          </div>
        )}
        <div className="mb-3 flex flex-wrap gap-2">
          <button data-testid="gl-adopt-hoa" disabled={readOnly || busy}
            onClick={() => run(() => api.post(`/tenants/${tenantId}/field-services/gl-codes/adopt-template`,
              { source: "hoa" }), "Standard chart added")}
            className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Sparkles className="h-4 w-4" /> Use the standard {V.one("org_short")} chart
          </button>
          {companyId && (
            <button data-testid="gl-adopt-company" disabled={readOnly || busy}
              onClick={() => run(() => api.post(`/tenants/${tenantId}/field-services/gl-codes/adopt-template`,
                { source: "company" }), "Management company's chart added")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
              <Building2 className="h-4 w-4" /> Use the management company's chart
            </button>
          )}
          <button data-testid="gl-import" disabled={readOnly || busy} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
            <Upload className="h-4 w-4" /> Import a CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={upload} className="hidden" data-testid="gl-file" />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-xl border border-sand-200">
          {codes.codes.map((c) => (
            <div key={c.code} data-testid={`gl-row-${c.code}`}
              className="flex items-center justify-between gap-2 border-b border-sand-100 px-3 py-2 last:border-0">
              <div className="min-w-0 flex-1">
                <span className="font-mono text-sm font-bold text-ink">{c.code}</span>
                <span className="ml-2 text-sm text-ink/70">{c.name}</span>
                {c.inherited && <span className="ml-2 rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold uppercase text-ink/45">from the company</span>}
              </div>
              {!readOnly && !c.inherited && (
                <button data-testid={`gl-del-${c.code}`} disabled={busy}
                  onClick={() => run(() => api.delete(`/tenants/${tenantId}/field-services/gl-codes/${c.code}`), "Removed")}
                  className="rounded-lg p-1.5 text-ink/25 hover:bg-redline-50 hover:text-redline-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className="mt-3 flex flex-wrap gap-2">
            <input data-testid="gl-new-code" value={newCode.code} placeholder="5100"
              onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
              className="w-24 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <input data-testid="gl-new-name" value={newCode.name} placeholder="What it's for"
              onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
              className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid="gl-add" disabled={busy || !newCode.code || !newCode.name}
              onClick={() => run(async () => {
                await api.post(`/tenants/${tenantId}/field-services/gl-codes`, newCode);
                setNewCode({ code: "", name: "" });
              }, "Added")}
              className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        )}
      </Card>

      <Card title="Which code a new job gets" testid="gl-defaults-card"
        sub="Set it once per trade and coding stops being a chore — a landscaping job arrives already charged to the landscaping line.">
        <div className="grid gap-2 sm:grid-cols-2">
          {Object.entries(CAT_LABEL).map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-2 rounded-xl border border-sand-200 px-3 py-2">
              <span className="min-w-0 truncate text-sm text-ink/70">{label}</span>
              <select data-testid={`gl-default-${k}`} value={defaults[k] || ""} disabled={readOnly}
                onChange={(e) => setDefaults({ ...defaults, [k]: e.target.value })}
                className="w-40 shrink-0 rounded-lg border border-sand-300 bg-white px-2 py-1.5 text-xs outline-none">
                <option value="">— none —</option>
                {codes.codes.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
              </select>
            </label>
          ))}
        </div>
        {!readOnly && (
          <button data-testid="gl-defaults-save" disabled={busy}
            onClick={() => run(() => api.put(`/tenants/${tenantId}/field-services/gl-defaults`,
              { gl_defaults: defaults }), "Saved")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Save className="h-4 w-4" /> Save the defaults
          </button>
        )}
      </Card>

      </>)}

      <Card title="Approvals & money" testid="approvals-card"
        sub={`Where a manager's decision stops and the ${V.grp()}'s begins. Both awards and invoices use the same line.`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">{V.Grp()} signs off at or above</span>
            <input data-testid="set-threshold" type="number" min="0" disabled={readOnly}
              value={st.board_threshold ?? ""} onChange={(e) => setSt({ ...st, board_threshold: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            <span className="mt-1 block text-xs text-ink/45">
              Currently {usd(st.board_threshold)}. Change orders count against the running total, so a
              small award can't be grown past the {V.grp()} with add-ons.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">Skip the contract under</span>
            <input data-testid="set-no-contract" type="number" min="0" disabled={readOnly}
              value={st.no_contract_under ?? ""} onChange={(e) => setSt({ ...st, no_contract_under: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            <span className="mt-1 block text-xs text-ink/45">Small jobs don't need paper — the work order is the record.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">Paperwork window after an award</span>
            <input data-testid="set-window" type="number" min="1" max="30" disabled={readOnly}
              value={st.paperwork_window_days ?? ""} onChange={(e) => setSt({ ...st, paperwork_window_days: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            <span className="mt-1 block text-xs text-ink/45">Business days. Miss it and we offer you the runner-up.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">Send export batches to</span>
            <input data-testid="set-ap-email" type="email" disabled={readOnly} placeholder="ap@yourcompany.com"
              value={st.ap_email || ""} onChange={(e) => setSt({ ...st, ap_email: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </label>
        </div>
        {accounting && fee && fee.mode !== "none" && (
          <p className="mt-3 rounded-xl bg-sand-100 px-4 py-2.5 text-xs text-ink/60" data-testid="fee-note">
            Your management company charges {fee.mode === "pct" ? `${fee.value}%` : usd(fee.value)} on
            {fee.applies_to === "project" ? " project work" : " all work"} over {usd(fee.min_amount)}, coded
            to {fee.gl_code}. It's disclosed at kickoff and exported as its own line — never shown to the vendor.
          </p>
        )}
        {!readOnly && (
          <button data-testid="approvals-save" disabled={busy} onClick={() => saveSettings({
            board_threshold: st.board_threshold, no_contract_under: st.no_contract_under,
            paperwork_window_days: st.paperwork_window_days, ap_email: st.ap_email,
          }, "Saved")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Save className="h-4 w-4" /> Save
          </button>
        )}
      </Card>

      <Card title="What insurance this property requires" icon={ShieldCheck} testid="coi-req-card"
        sub="Checked when a company tries to bid and again every time they're committed to a job. A lapsed certificate means off the property — no exceptions, no emergency override.">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">Minimum general liability</span>
            <input data-testid="coi-min-gl" type="number" min="0" step="100000" disabled={readOnly}
              value={st.coi_requirements?.min_general_liability ?? ""}
              onChange={(e) => setSt({ ...st, coi_requirements: { ...st.coi_requirements, min_general_liability: e.target.value } })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </label>
          <label className="flex items-center gap-2 self-end rounded-xl border border-sand-200 px-3 py-2.5">
            <input type="checkbox" data-testid="coi-addl" disabled={readOnly}
              checked={!!st.coi_requirements?.require_additional_insured}
              onChange={(e) => setSt({ ...st, coi_requirements: { ...st.coi_requirements, require_additional_insured: e.target.checked } })} />
            <span className="text-sm text-ink/70">Name us as additional insured</span>
          </label>
          <label className="flex items-center gap-2 self-end rounded-xl border border-sand-200 px-3 py-2.5">
            <input type="checkbox" data-testid="coi-wc" disabled={readOnly}
              checked={!!st.coi_requirements?.require_workers_comp}
              onChange={(e) => setSt({ ...st, coi_requirements: { ...st.coi_requirements, require_workers_comp: e.target.checked } })} />
            <span className="text-sm text-ink/70">Workers' comp required</span>
          </label>
        </div>
        <p className="mt-2 text-xs text-ink/45">
          Registering, hearing about work and coming out for a site visit need none of this — that's what
          keeps new companies in the funnel. Paperwork is what buys the right to bid and to be given a job.
        </p>
        {!readOnly && (
          <button data-testid="coi-req-save" disabled={busy}
            onClick={() => saveSettings({ coi_requirements: st.coi_requirements }, "Saved")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Save className="h-4 w-4" /> Save
          </button>
        )}
      </Card>

      <Card title="Who signs for the association" icon={PenLine} testid="signers-card"
        sub="Vendor contracts are countersigned by these people, in this order — the vendor always signs first.">
        <div className="space-y-2">
          {staff.filter((s) => ["board_member", "hoa_manager", "regional_manager"].includes(s.role)).map((s) => {
            const on = (st.designated_signers || []).some((x) => x.user_id === s.user_id);
            return (
              <label key={s.user_id} data-testid={`signer-${s.user_id}`}
                className="flex items-center gap-2.5 rounded-xl border border-sand-200 px-3 py-2.5">
                <input type="checkbox" checked={on} disabled={readOnly} onChange={(e) => {
                  const next = e.target.checked
                    ? [...(st.designated_signers || []), { user_id: s.user_id, title: "" }]
                    : (st.designated_signers || []).filter((x) => x.user_id !== s.user_id);
                  setSt({ ...st, designated_signers: next });
                }} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{s.name}</span>
                  <span className="block text-xs text-ink/45">{s.email} · {String(s.role).replace(/_/g, " ")}</span>
                </span>
              </label>
            );
          })}
          {!staff.length && <p className="text-xs text-ink/45">No {V.lcs("board")} or managers on this {V.lc("org")} yet.</p>}
        </div>
        {!readOnly && (
          <button data-testid="signers-save" disabled={busy}
            onClick={() => saveSettings({ designated_signers: st.designated_signers || [] }, "Saved")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Save className="h-4 w-4" /> Save
          </button>
        )}
      </Card>

      {/* iter370/371 — Redline drafts no contracts, so the "Contract wording" card is gone. The one
          switch it carried lives here. */}
      {!readOnly && (
        <label data-testid="mask-public-card" className="flex items-start gap-2.5 rounded-2xl border border-sand-200 bg-white px-4 py-3 text-xs font-semibold text-ink/60">
          <input type="checkbox" data-testid="mask-public" className="mt-0.5" checked={!!st.mask_public_name}
            onChange={(e) => saveSettings({ mask_public_name: e.target.checked },
              e.target.checked ? `${V.one("org")} name hidden on public listings` : `${V.one("org")} name shown publicly`)} />
          <span>Hide our name on public job listings until a company registers</span>
        </label>
      )}
    </div>
  );
}
